import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import clsx from 'clsx'
import { ReactElement, ReactNode } from 'react'

export type TooltipPlacement = 'top' | 'top-start' | 'top-end' | 'bottom' | 'left' | 'right'

export interface TooltipProps {
    title: ReactNode
    supportingText?: ReactNode
    placement?: TooltipPlacement
    arrow?: boolean
    open?: boolean
    defaultOpen?: boolean
    onOpenChange?: (open: boolean) => void
    delayDuration?: number
    children: ReactElement
    className?: string
}

type TooltipSide = 'top' | 'bottom' | 'left' | 'right'
type TooltipAlign = 'start' | 'center' | 'end'

const PLACEMENT_MAP: Record<TooltipPlacement, { side: TooltipSide; align: TooltipAlign }> = {
    top: { side: 'top', align: 'center' },
    'top-start': { side: 'top', align: 'start' },
    'top-end': { side: 'top', align: 'end' },
    bottom: { side: 'bottom', align: 'center' },
    left: { side: 'left', align: 'center' },
    right: { side: 'right', align: 'center' },
}

export const Tooltip = ({
    title,
    supportingText,
    placement = 'top',
    arrow = true,
    open,
    defaultOpen,
    onOpenChange,
    delayDuration = 200,
    children,
    className,
}: TooltipProps) => {
    const { side, align } = PLACEMENT_MAP[placement]
    const hasSupportingText = supportingText !== undefined && supportingText !== null

    return (
        <TooltipPrimitive.Provider delayDuration={delayDuration}>
            <TooltipPrimitive.Root
                open={open}
                defaultOpen={defaultOpen}
                onOpenChange={onOpenChange}>
                <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                        side={side}
                        align={align}
                        sideOffset={6}
                        arrowPadding={12}
                        collisionPadding={16}
                        className={clsx(
                            'z-50 rounded-radius-md bg-background-bg-brand-dark font-body shadow-lg',
                            hasSupportingText
                                ? 'flex max-w-width-xxs flex-col gap-spacing-xxs p-spacing-lg'
                                : 'px-spacing-lg py-spacing-md',
                            className,
                        )}>
                        <p
                            className={clsx(
                                'm-spacing-none text-xs font-semibold text-text-white',
                                !hasSupportingText && 'whitespace-nowrap text-center',
                            )}>
                            {title}
                        </p>
                        {hasSupportingText && (
                            <p className="m-spacing-none text-xs font-medium text-neutral-300">
                                {supportingText}
                            </p>
                        )}
                        {arrow && (
                            <TooltipPrimitive.Arrow
                                className="fill-background-bg-brand-dark"
                                width={16}
                                height={6}
                            />
                        )}
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    )
}
